import { FC, useCallback } from 'react'
import classNames from 'classnames'
import { LinkButton } from '@/atoms/Button'
import { Image } from '@/atoms/Image'
import { CaptionSM, LabelMD, TitleSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { ProjectHoverContent } from '@/services/ProjectsService/types'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'

interface CtaBoxProps {
  className?: string
  hoverContent?: ProjectHoverContent | null
  isGuildMember?: boolean
  isInTheaters?: boolean
  currentlyStreaming?: boolean
  isGuildGated?: boolean
  hasFreeTickets?: boolean
  canSeeGuildShareButton?: boolean
  shareCtaPath?: string | null
}

interface CtaContent {
  title?: string | null
  ctaText?: string | null
  ctaPath?: string | null
  description?: string | null
  image?: string | null
}

export const CtaBox: FC<CtaBoxProps> = ({
  className,
  hoverContent,
  isGuildMember,
  isInTheaters,
  currentlyStreaming,
  isGuildGated,
  hasFreeTickets,
  canSeeGuildShareButton,
  shareCtaPath,
}) => {
  const track = useSafeTrack()

  const handleButtonClick = useCallback(
    (ctaPath?: string | null) => {
      track('Watch Hover CTA Clicked', {
        ctaPath,
        projectSlug: hoverContent?.project?.slug,
        contentType: hoverContent?.__typename,
      })
    },
    [hoverContent?.__typename, hoverContent?.project?.slug, track],
  )

  const posterImageUrl =
    hoverContent?.angelKeyArt1?.cloudinaryPath ??
    hoverContent?.angelKeyArt2?.cloudinaryPath ??
    hoverContent?.angelKeyArt3?.cloudinaryPath ??
    ''

  const getCtaContent = (): CtaContent => {
    const projectSlug = hoverContent?.project?.slug ?? ''

    if (isGuildMember) {
      if (isInTheaters) {
        return {
          title: 'Claim tickets for movie night out',
          ctaText: 'Get Showtimes',
          ctaPath: `${paths.tickets.index}/${projectSlug}`,
          description: 'Get your 2 FREE tickets & watch this movie at your local theater.',
          image: 'tickets',
        }
      }

      if (!hasFreeTickets) {
        return {
          title: 'Upgrade for local movie tickets',
          ctaText: 'Upgrade Today!',
          ctaPath: paths.guild.pricing,
          description: 'Get your 2 FREE tickets & watch this movie at your local theater.',
          image: 'tickets',
        }
      }

      if (currentlyStreaming && canSeeGuildShareButton) {
        return {
          title: 'Share free access',
          ctaText: 'Share Free Access',
          ctaPath: shareCtaPath ?? '',
          description:
            'Generate a shareable link that allows friends and family to watch this film for FREE. They can watch obligation free.',
          image: 'key-art',
        }
      } else {
        return {
          title: 'Gift the Guild',
          ctaText: 'Gift the Guild',
          ctaPath: paths.external.giftGuild,
          description:
            'Gift an Angel Guild membership with a friend. Share the amazing Angel content with others and share light amplifying content.',
          image: 'key-art',
        }
      }
    } else {
      if (isInTheaters) {
        return {
          title: 'Join to watch & access benefits',
          ctaText: 'Claim Tickets',
          ctaPath: `${paths.guild.join}/${projectSlug}`,
          description: 'Get your 2 FREE tickets & watch this movie at your local theater.',
          image: 'tickets',
        }
      }

      if (isGuildGated) {
        return {
          title: 'Join to watch & access benefits',
          ctaText: 'Get Started',
          ctaPath: `${paths.guild.join}/${projectSlug}`,
          description:
            'Stream Ad-Free, Vote on concepts and films to decide what we greenlight next, get two tickets to every theatrical release and save 20% on Angel merch.',
          image: 'none',
        }
      }
    }

    return {
      title: null,
      ctaText: null,
      ctaPath: null,
      description: null,
    }
  }

  const ctaContent = getCtaContent()

  if (!ctaContent.title && !ctaContent.description && !ctaContent.ctaPath && !ctaContent.ctaText) return null
  //TODO add translations for this component if this wins

  return (
    <div
      className={classNames('flex h-fit flex-row gap-4 rounded-2xl p-4 lg:min-w-[412px] bg-core-gray-800', className)}
      style={{
        border: '1px solid rgba(255, 255, 255, 0.16)',
      }}
    >
      {ctaContent?.image === 'key-art' && (
        <Image
          src={getCloudinaryImageUrl({
            path: posterImageUrl,
            width: 97,
            height: 146,
            transforms: 'c_fill',
          })}
          alt={`${hoverContent?.name} poster`}
          width={97}
          height={146}
          className="h-[146px] w-[97px] rounded-xl object-cover"
        />
      )}
      {ctaContent?.image === 'tickets' && (
        <Image
          src={getCloudinaryImageUrl({
            path: '/v1752076351/angel-studios/watch/hover/image_24.webp',
            width: 154,
            height: 146,
            transforms: 'c_fill',
          })}
          alt={'Guild Tickets'}
          width={154}
          height={146}
          className="h-[146px] w-[154px] rounded-xl object-cover"
        />
      )}
      <div className="flex flex-col gap-2">
        {ctaContent?.title && (
          <TitleSM className="bg-guild-gradient bg-clip-text text-transparent" weight="bold">
            {ctaContent.title}
          </TitleSM>
        )}
        {ctaContent.description && <CaptionSM color="core-gray-400">{ctaContent.description}</CaptionSM>}
        {ctaContent?.ctaPath && ctaContent?.ctaText && (
          <LinkButton
            href={ctaContent.ctaPath ?? ''}
            variant="black"
            onClick={() => handleButtonClick(ctaContent.ctaPath)}
            className={classNames(
              'flex items-center gap-[6px] mt-2 py-[11px] px-5 w-full max-h-12 rounded-xl mb-4 border-0 bg-[#626363] backdrop-blur-3xl',
            )}
          >
            {/* {secondaryCtaIcon && determineButtonIcon(secondaryCtaIcon, 'white')} */}
            <LabelMD weight="semibold" className="mt-[2.25px]">
              {ctaContent.ctaText}
            </LabelMD>
          </LinkButton>
        )}
      </div>
    </div>
  )
}
